import { cn } from "@/lib/utils";

function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={cn(
        "relative overflow-hidden rounded-md bg-muted/60",
        "before:absolute before:inset-0 before:-translate-x-full",
        "before:animate-[shimmer_1.5s_infinite]",
        "before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent",
        className
      )} 
      {...props} 
    />
  );
}

// Card skeleton for stats
function SkeletonCard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={cn(
        "rounded-xl border border-border/50 bg-card p-5",
        className
      )}
      {...props}
    >
      <div className="flex flex-col items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-xl" />
        <Skeleton className="w-16 h-8" />
        <Skeleton className="w-20 h-3" />
      </div>
    </div>
  );
}

// Document row skeleton
function SkeletonDocumentRow({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={cn(
        "rounded-xl border border-border/50 bg-card p-4 flex items-center gap-4",
        className
      )}
      {...props}
    >
      <Skeleton className="w-11 h-11 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <div className="flex gap-2">
          <Skeleton className="h-3 w-16 rounded-full" />
          <Skeleton className="h-3 w-20 rounded-full" />
          <Skeleton className="h-3 w-14" />
        </div>
      </div>
      <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
    </div>
  );
}

// Document grid skeleton
function SkeletonDocumentGrid({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={cn(
        "rounded-xl border border-border/50 bg-card p-4 space-y-3",
        className
      )}
      {...props}
    >
      <Skeleton className="w-full aspect-[4/3] rounded-lg" />
      <Skeleton className="h-4 w-4/5" />
      <div className="flex gap-2">
        <Skeleton className="h-3 w-12 rounded-full" />
        <Skeleton className="h-3 w-16 rounded-full" />
      </div>
    </div>
  );
}

// Search bar skeleton
function SkeletonSearchBar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={cn(
        "rounded-2xl border border-border/30 bg-card/50 p-1.5",
        className
      )}
      {...props}
    >
      <div className="flex gap-2">
        <div className="flex-1 h-12 md:h-14 rounded-xl bg-muted/40 flex items-center px-4 gap-3">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="w-14 h-12 md:h-14 rounded-xl" />
      </div>
    </div>
  );
}

// Full dashboard skeleton
function SkeletonDashboard() {
  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
      </div>
      
      {/* Search */}
      <SkeletonSearchBar />
      
      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
      
      {/* Content grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Health widget skeleton */}
          <div className="rounded-xl border border-border/50 bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-8 w-16 rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-2 w-full rounded-full" />
                </div>
              ))}
            </div>
          </div>
          
          {/* Recent docs */}
          <div className="rounded-xl border border-border/50 bg-card">
            <div className="p-4 border-b border-border/50">
              <Skeleton className="h-5 w-40" />
            </div>
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="w-10 h-10 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <div className="flex gap-2">
                      <Skeleton className="h-3 w-16 rounded-full" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right column */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border/50 bg-card p-6 space-y-4">
            <Skeleton className="h-5 w-24" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="w-8 h-8 rounded-lg" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Full documents list skeleton
function SkeletonDocumentsList({ count = 8 }: { count?: number }) {
  return (
    <div className="space-y-2 animate-in fade-in duration-500">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonDocumentRow key={i} style={{ animationDelay: `${i * 50}ms` }} />
      ))}
    </div>
  );
}

// Documents grid skeleton
function SkeletonDocumentsGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 animate-in fade-in duration-500">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonDocumentGrid key={i} style={{ animationDelay: `${i * 50}ms` }} />
      ))}
    </div>
  );
}

export { 
  Skeleton, 
  SkeletonCard, 
  SkeletonDocumentRow, 
  SkeletonDocumentGrid,
  SkeletonSearchBar,
  SkeletonDashboard,
  SkeletonDocumentsList,
  SkeletonDocumentsGrid
};
